import { useQuery } from '@tanstack/react-query'
import { Moon, Sun } from 'lucide-react'
import { adminMe } from '@/lib/api'
import { Skeleton } from '@/components/ui/skeleton'
import { useTheme } from '@/contexts/ThemeContext'
import { cn } from '@/lib/cn'

export function Topbar({ title }: { title?: string }) {
  const { data: me } = useQuery({
    queryKey: ['admin-me'],
    queryFn: adminMe,
    staleTime: Infinity,
  })
  const { theme, toggleTheme, isDark } = useTheme()

  return (
    <header
      className={cn(
        'fixed right-0 left-60 top-0 z-30 flex h-14 items-center justify-between border-b px-6 backdrop-blur',
        isDark
          ? 'border-white/10 bg-[#0A0A0A]/80 text-white'
          : 'border-border bg-background/80',
      )}
    >
      {title && <h1 className="text-sm font-semibold">{title}</h1>}
      <div className="ml-auto flex items-center gap-3">
        <button
          type="button"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
            isDark ? 'hover:bg-white/10 text-gray-300' : 'hover:bg-accent text-muted-foreground',
          )}
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>

        {me ? (
          <div className="text-right">
            <p className="text-xs font-medium leading-none">{me.display_name ?? me.email}</p>
            <p className={cn('mt-0.5 text-xs capitalize', isDark ? 'text-gray-400' : 'text-muted-foreground')}>
              {me.role.replace('_', ' ')}
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-2.5 w-16" />
          </div>
        )}
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold',
            isDark ? 'bg-[#A2FF00]/10 text-[#A2FF00]' : 'bg-primary/10 text-primary',
          )}
        >
          {me?.display_name?.[0] ?? me?.email?.[0]?.toUpperCase() ?? '?'}
        </div>
      </div>
    </header>
  )
}
